import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const questions = [
  {
    id: 1,
    question: 'What\'s your living situation?',
    emoji: '🏠',
    options: ['Small apartment', 'Large house with yard', 'Shared home', 'Studio / single room'],
  },
  {
    id: 2,
    question: 'How active is your lifestyle?',
    emoji: '🏃',
    options: ['Very active — always on the go', 'Moderately active', 'Mostly relaxed at home', 'Couch potato and proud'],
  },
  {
    id: 3,
    question: 'How much time can you dedicate to your cat daily?',
    emoji: '⏰',
    options: ['Less than 1 hour', '1–2 hours', '3–4 hours', 'All day, I work from home'],
  },
  {
    id: 4,
    question: 'Do you have other pets or children at home?',
    emoji: '👨‍👩‍👧',
    options: ['No, just me', 'Other cats', 'Dogs', 'Young children'],
  },
  {
    id: 5,
    question: 'What personality do you prefer in a cat?',
    emoji: '😸',
    options: ['Cuddly & affectionate', 'Playful & energetic', 'Independent & calm', 'Curious & adventurous'],
  },
  {
    id: 6,
    question: 'How do you feel about cat hair?',
    emoji: '🪮',
    options: ['No problem at all!', 'A little is fine', 'Prefer minimal shedding', 'Allergic — need hypoallergenic'],
  },
  {
    id: 7,
    question: 'What age cat are you open to?',
    emoji: '🐱',
    options: ['Kitten (under 1 year)', 'Young adult (1–3 years)', 'Adult (3–7 years)', 'Senior (7+ years)'],
  },
  {
    id: 8,
    question: 'What\'s your experience with cats?',
    emoji: '🎓',
    options: ['First-time cat owner', 'Had cats growing up', 'Current cat owner', 'Experienced multi-cat household'],
  },
  {
    id: 9,
    question: 'What matters most to you in a cat?',
    emoji: '💖',
    options: ['Companionship & bonding', 'Entertainment & fun', 'Low maintenance', 'Unique looks or rare breed'],
  },
  {
    id: 10,
    question: 'Where would your cat mostly spend time?',
    emoji: '🛋️',
    options: ['Indoors only', 'Mostly indoors, some outdoor', 'Free roam indoor/outdoor', 'Outdoor with shelter'],
  },
];

const catResults = [
  {
    breed: 'Ragdoll',
    emoji: '😻',
    description: 'You\'re a perfect match for a Ragdoll! These gentle giants are famously docile, affectionate, and love to be held. They go limp with joy in your arms — hence the name.',
    traits: ['Cuddly', 'Gentle', 'Quiet', 'Indoor'],
    image: 'https://public.readdy.ai/ai/img_res/0e119ef81b8968943ef1fb1eed00aa62.jpg',
  },
  {
    breed: 'Bengal',
    emoji: '🐆',
    description: 'You\'re made for a Bengal! Wild-looking but deeply loving, Bengals are athletic, playful, and endlessly entertaining. They thrive with active owners who love adventure.',
    traits: ['Energetic', 'Playful', 'Smart', 'Athletic'],
    image: 'https://readdy.ai/api/search-image?query=stunning%20Bengal%20cat%20with%20leopard%20spotted%20coat%2C%20bright%20green%20eyes%2C%20athletic%20muscular%20build%2C%20sitting%20on%20a%20natural%20wood%20surface%2C%20professional%20pet%20photography%2C%20dramatic%20lighting%2C%20wild%20and%20beautiful%20appearance&width=400&height=300&seq=bengal1&orientation=landscape',
  },
  {
    breed: 'British Shorthair',
    emoji: '🫐',
    description: 'The British Shorthair is your ideal companion! Calm, easygoing, and quietly affectionate — they love being near you without being clingy. Perfect for a relaxed lifestyle.',
    traits: ['Calm', 'Independent', 'Loyal', 'Low-maintenance'],
    image: 'https://readdy.ai/api/search-image?query=adorable%20British%20Shorthair%20cat%20with%20dense%20blue-gray%20plush%20coat%2C%20round%20chubby%20face%2C%20copper%20eyes%2C%20sitting%20calmly%20on%20a%20cozy%20blanket%2C%20professional%20pet%20photography%2C%20soft%20warm%20lighting%2C%20charming%20and%20peaceful%20expression&width=400&height=300&seq=british1&orientation=landscape',
  },
  {
    breed: 'Maine Coon',
    emoji: '🦁',
    description: 'A Maine Coon is your soulmate! These gentle giants are dog-like in loyalty, love families and other pets, and have a playful spirit that never fades. Big heart, big personality.',
    traits: ['Friendly', 'Sociable', 'Playful', 'Family-friendly'],
    image: 'https://readdy.ai/api/search-image?query=majestic%20Maine%20Coon%20cat%20with%20long%20flowing%20tabby%20fur%2C%20large%20tufted%20ears%2C%20bushy%20tail%2C%20sitting%20regally%20on%20a%20wooden%20floor%2C%20professional%20pet%20photography%2C%20natural%20warm%20lighting%2C%20impressive%20and%20noble%20appearance&width=400&height=300&seq=mainecoon1&orientation=landscape',
  },
  {
    breed: 'Siamese',
    emoji: '💬',
    description: 'You\'re destined for a Siamese! Vocal, social, and deeply bonded to their humans, Siamese cats are the ultimate conversation partners. They\'ll follow you everywhere and always have something to say.',
    traits: ['Vocal', 'Social', 'Intelligent', 'Devoted'],
    image: 'https://readdy.ai/api/search-image?query=elegant%20Siamese%20cat%20with%20striking%20blue%20eyes%2C%20cream%20and%20dark%20brown%20colorpoint%20coat%2C%20sleek%20slender%20body%2C%20sitting%20gracefully%20on%20a%20light%20background%2C%20professional%20pet%20photography%2C%20dramatic%20lighting%2C%20beautiful%20and%20expressive%20face&width=400&height=300&seq=siamese1&orientation=landscape',
  },
];

function getResult(answers: number[]): typeof catResults[0] {
  const sum = answers.reduce((a, b) => a + b, 0);
  const index = sum % catResults.length;
  return catResults[index];
}

export default function CatQuizSection() {
  const [started, setStarted] = useState(false);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<typeof catResults[0] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');

  const handleAnswer = (optionIndex: number) => {
    const newAnswers = [...answers, optionIndex];
    setAnswers(newAnswers);
    if (currentQ + 1 < questions.length) {
      setCurrentQ(currentQ + 1);
    } else {
      setShowEmailGate(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setEmailError('Please enter your email to see your result.');
      return;
    }
    setEmailError('');
    setSubmitting(true);

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);
      formData.append('name', name);
      formData.append('quiz_answers', answers.join(','));

      await fetch('https://readdy.ai/api/form/d6j4igmfjdhm2lq4t34g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
    } catch (_) {
      // silent
    }

    setResult(getResult(answers));
    setSubmitted(true);
    setSubmitting(false);
  };

  const handleRestart = () => {
    setStarted(false);
    setCurrentQ(0);
    setAnswers([]);
    setShowEmailGate(false);
    setEmail('');
    setName('');
    setSubmitted(false);
    setResult(null);
    setEmailError('');
  };

  const progress = started ? Math.round((currentQ / questions.length) * 100) : 0;

  return (
    <section className="py-20 bg-gradient-to-b from-rose-50 to-amber-50 relative overflow-hidden">
      {/* Decorative paws */}
      <div className="absolute top-8 left-8 text-rose-200 text-6xl opacity-40 pointer-events-none select-none">🐾</div>
      <div className="absolute bottom-8 right-8 text-amber-200 text-6xl opacity-40 pointer-events-none select-none">🐾</div>

      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block bg-rose-100 text-rose-600 text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-4">
            Purrsonality Quiz
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Find Your Purrfect Pet 🐱
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto">
            Answer 10 quick questions and we'll reveal the cat breed that matches your lifestyle perfectly.
          </p>
        </motion.div>

        {/* Card */}
        <motion.div
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <AnimatePresence mode="wait">

            {/* START SCREEN */}
            {!started && !submitted && (
              <motion.div
                key="start"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="p-10 text-center"
              >
                <div className="text-7xl mb-6">🐾</div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">Ready to meet your match?</h3>
                <p className="text-gray-500 mb-8 text-sm max-w-md mx-auto">
                  10 fun questions. 1 perfect cat breed. Your results are waiting — let's go!
                </p>
                <button
                  onClick={() => setStarted(true)}
                  className="px-10 py-4 bg-rose-500 hover:bg-rose-600 text-white font-bold text-base rounded-full transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
                >
                  Start the Quiz 🐱
                </button>
              </motion.div>
            )}

            {/* QUIZ QUESTIONS */}
            {started && !showEmailGate && !submitted && (
              <motion.div
                key={`q-${currentQ}`}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3 }}
                className="p-8 md:p-10"
              >
                {/* Progress */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-400 font-medium">Question {currentQ + 1} of {questions.length}</span>
                  <span className="text-xs text-rose-500 font-semibold">{progress}% complete</span>
                </div>
                <div className="w-full bg-rose-100 rounded-full h-2 mb-8">
                  <motion.div
                    className="bg-rose-500 h-2 rounded-full"
                    initial={{ width: `${progress}%` }}
                    animate={{ width: `${Math.round(((currentQ + 1) / questions.length) * 100)}%` }}
                    transition={{ duration: 0.4 }}
                  />
                </div>

                <div className="text-4xl mb-4 text-center">{questions[currentQ].emoji}</div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-800 text-center mb-8">
                  {questions[currentQ].question}
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {questions[currentQ].options.map((option, i) => (
                    <button
                      key={i}
                      onClick={() => handleAnswer(i)}
                      className="w-full text-left px-5 py-4 rounded-xl border-2 border-gray-100 hover:border-rose-400 hover:bg-rose-50 text-gray-700 text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap"
                    >
                      <span className="inline-block w-6 h-6 rounded-full bg-rose-100 text-rose-600 text-xs font-bold text-center leading-6 mr-3 flex-shrink-0">
                        {String.fromCharCode(65 + i)}
                      </span>
                      {option}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* EMAIL GATE */}
            {showEmailGate && !submitted && (
              <motion.div
                key="email-gate"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="p-8 md:p-10 text-center"
              >
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">You're almost there!</h3>
                <p className="text-gray-500 text-sm mb-8 max-w-sm mx-auto">
                  Your purrfect cat match is ready. Enter your email to reveal your result and join our cat-loving community!
                </p>

                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4 max-w-sm mx-auto text-left"
                >
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Your Name</label>
                    <input
                      type="text"
                      name="name"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="e.g. Sarah"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-rose-400 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email Address <span className="text-rose-500">*</span></label>
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-rose-400 transition"
                    />
                    {emailError && <p className="text-rose-500 text-xs mt-1">{emailError}</p>}
                  </div>
                  <p className="text-xs text-gray-400">
                    🔒 No spam, ever. Just cat content you'll love.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 bg-rose-500 hover:bg-rose-600 disabled:opacity-60 text-white font-bold text-sm rounded-xl transition-all duration-300 hover:scale-105 shadow-md whitespace-nowrap cursor-pointer"
                  >
                    {submitting ? 'Revealing your match...' : 'Reveal My Purrfect Match 🐾'}
                  </button>
                </form>
              </motion.div>
            )}

            {/* RESULT */}
            {submitted && result && (
              <motion.div
                key="result"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="p-8 md:p-10"
              >
                <div className="text-center mb-6">
                  <span className="inline-block bg-rose-100 text-rose-600 text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-3">
                    Your Perfect Match
                  </span>
                  <div className="text-5xl mb-2">{result.emoji}</div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {result.breed}
                  </h3>
                </div>

                <div className="flex flex-col md:flex-row gap-6 items-start">
                  <div className="w-full md:w-48 flex-shrink-0 rounded-xl overflow-hidden shadow-md">
                    <img
                      src={result.image}
                      alt={result.breed}
                      className="w-full h-40 object-cover object-top"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-gray-600 text-sm leading-relaxed mb-4">{result.description}</p>
                    <div className="flex flex-wrap gap-2 mb-6">
                      {result.traits.map(t => (
                        <span key={t} className="bg-rose-50 text-rose-600 text-xs font-semibold px-3 py-1 rounded-full border border-rose-100">
                          {t}
                        </span>
                      ))}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <a
                        href="/adopt"
                        className="flex-1 text-center py-3 bg-rose-500 hover:bg-rose-600 text-white font-bold text-sm rounded-xl transition-all duration-300 hover:scale-105 shadow-md whitespace-nowrap cursor-pointer"
                      >
                        🐾 Adopt a Cat Near You
                      </a>
                      <button
                        onClick={handleRestart}
                        className="flex-1 py-3 border-2 border-gray-200 hover:border-rose-300 text-gray-600 hover:text-rose-500 font-semibold text-sm rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer"
                      >
                        Retake Quiz
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
